//
//  GhaCsvExport.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { ReactElement, useState, SyntheticEvent } from "react";
import {
  Container,
  Alert,
  AlertColor,
  Snackbar,
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { SectionContainer, SectionRow } from "./component/Section";
import { DateRangePicker } from "./component/DateRangePicker";
import { GhaCsvSummarySelector } from "./component/GhaCsvSummarySelector";
import { GhaCsvIntradayButtonList } from "./component/GhaCsvIntradayButtonList";
import { FormButton } from "./component/FormButton";
import useActionAlert from "./hooks/useActionAlert";
import useDateRange from "./hooks/useDateRange";
import useGhaCsvSummarySelector from "./hooks/useGhaCsvSummarySelector";
import useGhaCsvExport from "./hooks/useGhaCsvExport";
import { getToday } from "./utils/getToday";
import { CONTACT_URL } from "./utils/env";
import BackToAdminButton from "./component/BackToAminMainButton";
import GhaCsvDownloadList from "./GhaCsvDownloadList";
import { PageTitle } from "./component/PageTitle";
import TitleBar from "./component/TitleBar";

const GhaCsvExport = (): ReactElement => {
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const { message, type, showMessage } = useActionAlert();
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    errorMsg: dateRangeErrorMsg,
  } = useDateRange(
    getToday().subtract(7, "day"),
    getToday().subtract(1, "day")
  );
  const {
    selectedGhaCsvSummaryItems,
    setSelectedGhaCsvSummaryItems,
    errorMsg: summaryErrorMsg,
  } = useGhaCsvSummarySelector();
  const { handleGhaDailyCsvExport, handleGhaIntradayCsvExport, isExporting } =
    useGhaCsvExport(showMessage);

  const startDateStr = startDate ? startDate.format("YYYY-MM-DD") : "";
  const endDateStr = endDate ? endDate.format("YYYY-MM-DD") : "";
  const isDateInvalid = !!dateRangeErrorMsg;

  return (
    <>
      <TitleBar contactUrl={CONTACT_URL} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "100%",
          padding: "10px 0",
        }}
      >
        <BackToAdminButton />
      </Box>

      <Container
        sx={{
          my: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <PageTitle title="GHA CSVダウンロード" />
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: "700px",
            overflow: "visible",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "100%",
              maxWidth: "800px",
              mb: 4,
            }}
          >
            <Tabs value={tabIndex} onChange={handleTabChange} centered>
              <Tab label="CSV作成リクエスト" />
              <Tab label="作成済みCSV一覧" />
            </Tabs>
          </Box>

          {tabIndex === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  width: "100%",
                  bgcolor: "rgba(255, 255, 255, 0.85)",
                  backdropFilter: "blur(12px)",
                  pt: 4,
                  px: 4,
                  pb: 2,
                }}
              >
                <SectionContainer width="100%">
                  <SectionRow title="期間指定" direction="column">
                    <Box
                      display="flex"
                      justifyContent="center"
                      width="100%"
                      gap={2}
                    >
                      <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        errorMsg={dateRangeErrorMsg}
                      />
                    </Box>
                  </SectionRow>
                </SectionContainer>
              </Box>

              <Box sx={{ width: "100%", p: 4 }}>
                <SectionContainer width="100%">
                  <SectionRow title="日次データ" direction="column">
                    <Box width="100%">
                      <FormButton
                        button={
                          <Box
                            display="flex"
                            width="100%"
                            justifyContent="center"
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={
                                isDateInvalid || !!summaryErrorMsg || isExporting
                              }
                              onClick={() =>
                                handleGhaDailyCsvExport(
                                  startDateStr,
                                  endDateStr,
                                  selectedGhaCsvSummaryItems.join(",")
                                )
                              }
                              sx={{
                                width: 500,
                                minWidth: 0,
                                textTransform: "none",
                              }}
                            >
                              日次データをリクエスト
                            </Button>
                          </Box>
                        }
                      >
                        <GhaCsvSummarySelector
                          selectedItems={selectedGhaCsvSummaryItems}
                          setSelectedItems={setSelectedGhaCsvSummaryItems}
                          errorMsg={summaryErrorMsg}
                        />
                      </FormButton>
                    </Box>
                  </SectionRow>

                  <SectionRow title="時系列データ" direction="column">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      ダウンロードしたいデータ項目のリクエストボタンを押してください。
                    </Typography>
                    <Box display="flex" width="100%" justifyContent="center">
                      <GhaCsvIntradayButtonList
                        startDate={startDateStr}
                        endDate={endDateStr}
                        disabled={isDateInvalid || isExporting}
                        onRequest={handleGhaIntradayCsvExport}
                      />
                    </Box>
                  </SectionRow>
                </SectionContainer>
              </Box>
            </Box>
          )}

          {tabIndex === 1 && (
            <Box width="100%" maxWidth="md" pb={4}>
              <GhaCsvDownloadList />
            </Box>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={!!message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={type as AlertColor} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GhaCsvExport;
